export const PRE_BOOTSTRAP_LOADER_ID = 'pre-bootstrap-loader';
export const PRE_BOOTSTRAP_LOADER_OUT_CLASS = 'pre-bootstrap-loader-out';
/** Matches the opacity transition in the apps' animations.scss. */
export const PRE_BOOTSTRAP_LOADER_FADE_MS = 200;
/** Name of the keyframes that reveal the lockup after the initial hidden delay. */
export const PRE_BOOTSTRAP_LOADER_REVEAL_ANIMATION = 'pre-bootstrap-reveal';

const DISMISSED_ATTR = 'data-dismissed';

/**
 * Fades out and removes the static loader that index.html shows before the app
 * boots. It leaves the accessibility tree and stops taking pointer events at
 * once. With `finishIntro`, a lockup that is already showing finishes its intro
 * before the fade, so a following loader takes over from the finished lockup;
 * otherwise, or while the lockup is still hidden, the fade starts at once. The
 * element is removed after the fade.
 */
export function dismissPreBootstrapLoader(options: { finishIntro?: boolean } = {}, doc: Document = document): void {
    const loader = doc.getElementById(PRE_BOOTSTRAP_LOADER_ID);
    if (!loader || loader.hasAttribute(DISMISSED_ATTR) || loader.classList.contains(PRE_BOOTSTRAP_LOADER_OUT_CLASS)) {
        return;
    }
    loader.setAttribute(DISMISSED_ATTR, '');
    loader.removeAttribute('role');
    loader.setAttribute('aria-hidden', 'true');
    const timers = doc.defaultView ?? globalThis;
    const fadeOut = (): void => {
        loader.classList.add(PRE_BOOTSTRAP_LOADER_OUT_CLASS);
        timers.setTimeout(() => loader.remove(), PRE_BOOTSTRAP_LOADER_FADE_MS);
    };
    const wait = options.finishIntro ? remainingIntroMs(loader) : 0;
    if (wait > 0) {
        timers.setTimeout(fadeOut, wait);
    } else {
        fadeOut();
    }
}

/** Time left in the loader's intro animations; 0 while the lockup is still hidden or has no intro. */
function remainingIntroMs(loader: HTMLElement): number {
    if (typeof loader.getAnimations !== 'function') {
        return 0;
    }
    const animations = loader.getAnimations({ subtree: true });
    const reveal = animations.find((animation) => animationName(animation) === PRE_BOOTSTRAP_LOADER_REVEAL_ANIMATION);
    if (!reveal || (reveal.effect?.getComputedTiming().progress ?? null) === null) {
        return 0;
    }
    let remaining = 0;
    for (const animation of animations) {
        const current = animation.currentTime;
        const end = animation.effect?.getComputedTiming().endTime;
        if (typeof current === 'number' && typeof end === 'number' && Number.isFinite(end)) {
            remaining = Math.max(remaining, end - current);
        }
    }
    return Math.ceil(remaining);
}

function animationName(animation: Animation): string | undefined {
    return 'animationName' in animation ? (animation as CSSAnimation).animationName : undefined;
}
